import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api'
import type { CreatePurchasePayload, Purchase } from '@/api'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/contexts/ToastContext'
import { queryKeys } from '@/lib/queryKeys'

/** Mensagem exibida na tarja vermelha quando falta login. */
export const NOT_LOGGED_MESSAGE =
  'Você precisa entrar na sua conta para adicionar produtos comprados.'

/** Lista de compras do usuário. Vazia e sem requisição quando deslogado. */
export function usePurchases() {
  const { isAuthenticated } = useAuth()

  const query = useQuery({
    queryKey: queryKeys.purchases,
    queryFn: api.purchases.listPurchases,
    enabled: isAuthenticated,
  })

  const purchases: Purchase[] = query.data ?? []

  return {
    ...query,
    purchases,
    lastWeek: api.purchases.filterLastWeek(purchases),
    /** Ids de produto já comprados — o card usa para mostrar o estado marcado. */
    purchasedProductIds: new Set(purchases.map((p) => p.product.id)),
  }
}

/**
 * Registra uma compra. Cuida sozinho do caso "não logado", devolvendo a
 * mensagem para a UI exibir em vez de estourar um erro cru.
 */
export function useAddPurchase() {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuth()
  const { notify } = useToast()

  const mutation = useMutation({
    mutationFn: (payload: CreatePurchasePayload) => api.purchases.createPurchase(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchases })
      notify('Compra adicionada! Você já está concorrendo.', 'success')
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) return
      notify(
        error instanceof ApiError ? error.message : 'Não foi possível adicionar a compra.',
        'error',
      )
    },
  })

  return {
    ...mutation,
    /** true quando a última tentativa foi bloqueada por falta de sessão. */
    requiresLogin:
      !isAuthenticated ||
      (mutation.error instanceof ApiError && mutation.error.status === 401),
  }
}

export function useRemovePurchase() {
  const queryClient = useQueryClient()
  const { notify } = useToast()

  return useMutation({
    mutationFn: (id: string) => api.purchases.removePurchase(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.purchases })
      notify('Produto removido da sua lista.', 'info')
    },
  })
}
